import React, { useEffect, useMemo, useState } from 'react'
import API from '../../../../services/API'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

const employmentTypeOptions = ['PAYE', 'Invoice']

const employeeName = (employee) =>
  `${employee?.userAccount?.firstName ?? ''} ${
    employee?.userAccount?.lastName ?? ''
  }`.trim() || 'Unknown employee'

const MissingPaymentTypeWarning = ({ employees, loading, onSaved }) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [draftTypes, setDraftTypes] = useState({})

  useEffect(() => {
    setDraftTypes(
      employees.reduce(
        (acc, employee) => ({
          ...acc,
          [employee._id]: employee.employmentType ?? '',
        }),
        {},
      ),
    )
  }, [employees])

  const updates = useMemo(
    () =>
      employees
        .map((employee) => ({
          employee: employee._id,
          employmentType: draftTypes[employee._id] ?? '',
          originalEmploymentType: employee.employmentType ?? '',
        }))
        .filter(
          (update) =>
            update.employmentType &&
            update.employmentType !== update.originalEmploymentType,
        ),
    [draftTypes, employees],
  )

  const savePaymentTypes = () => {
    if (!updates.length) return
    setSaving(true)
    setSaveError(null)
    API.post('/payroll/payment-types', { updates })
      .then((res) => {
        if (res.status === 200) {
          setOpen(false)
          onSaved?.()
        } else {
          setSaveError(
            res.data?.error ??
              'Unable to save the payment types. Please try again.',
          )
        }
      })
      .catch(() =>
        setSaveError('Unable to save the payment types. Please try again.'),
      )
      .finally(() => setSaving(false))
  }

  if (!loading && employees.length === 0) return null

  return (
    <>
      <Alert
        severity="warning"
        variant="outlined"
        action={
          <Button color="inherit" size="small" onClick={() => setOpen(true)}>
            Review
          </Button>
        }
      >
        {loading
          ? 'Checking employee payment types...'
          : `${employees.length} employee${
              employees.length === 1 ? '' : 's'
            } need a PAYE or invoice worker type before payroll totals can be trusted.`}
      </Alert>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Review Payment Types</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Alert severity="info" variant="outlined">
              Set each employee's employment type so AmbuNet can group pay-run
              totals as PAYE or invoice/remittance work.
            </Alert>
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}
            {loading ? (
              <LinearProgress />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>PIN</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell width={260}>Employment Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow key={employee._id}>
                        <TableCell>
                          <Stack gap={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              {employeeName(employee)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee?.userAccount?.email}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{employee.pinNumber || '-'}</TableCell>
                        <TableCell>{employee.status || '-'}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={draftTypes[employee._id] ?? ''}
                            onChange={(e) =>
                              setDraftTypes((prev) => ({
                                ...prev,
                                [employee._id]: e.target.value,
                              }))
                            }
                            fullWidth
                          >
                            <MenuItem value="" disabled>
                              Select type
                            </MenuItem>
                            {employmentTypeOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={savePaymentTypes}
            disabled={saving || !updates.length}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MissingPaymentTypeWarning
